import React, { useState } from "react";
import { formatLocal, formatLocalDateOnly } from "../utils/dateHelpers";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface WarmupDebugTabProps { data: any; }

function formatPhoneE164(v: unknown): string {
  if (!v) return "\u2014";
  let digits = String(v).replace(/\D/g, "");
  if (digits.length === 11 && digits[0] === "1") digits = digits.substring(1);
  if (digits.length === 10) return `+1 ${digits.substring(0, 3)} ${digits.substring(3, 6)} ${digits.substring(6)}`;
  return String(v);
}

function statusBadge(s: string | null | undefined): React.ReactNode {
  if (!s) return <span className="badge bg-secondary">{"\u2014"}</span>;
  const cls = s === "Active" ? "bg-success" : s === "Completed" ? "bg-primary" : s === "Paused" ? "bg-warning text-dark" : "bg-secondary";
  return <span className={`badge ${cls}`}>{s}</span>;
}

const WarmupDebugTab: React.FC<WarmupDebugTabProps> = ({ data }) => {
  const [expandedLogs, setExpandedLogs] = useState<Set<number>>(new Set());
  const toggleLog = (idx: number) => setExpandedLogs((p) => { const n = new Set(p); if (n.has(idx)) n.delete(idx); else n.add(idx); return n; });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const warmupJobs: any[] = data?.warmupJobs || [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const callLogs: any[] = data?.callLogs || [];

  return (
    <div style={{ paddingTop: 10 }}>
      {/* Warmup Jobs */}
      <h5>Warmup Jobs</h5>
      {warmupJobs.length === 0 ? (
        <div className="alert alert-info">This phone number is not in any warmup jobs.</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm table-striped align-middle">
            <thead>
              <tr>
                <th>Job ID</th><th>Name</th><th>Account</th><th>Job Status</th><th>Number Status</th>
                <th>Day</th><th>Calls Today</th><th>Total Calls</th><th>Last Called</th><th>Carriers</th><th>Start Date</th>
              </tr>
            </thead>
            <tbody>
              {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
              {warmupJobs.map((job: any, idx: number) => {
                const lastCalled = job.lastCalledOn ? formatLocal(new Date(job.lastCalledOn)) : "\u2014";
                const startDate = job.startDate ? formatLocalDateOnly(new Date(job.startDate)) : "\u2014";
                return (
                  <tr key={idx}>
                    <td>{job.warmupJobId}</td>
                    <td>{job.name || "\u2014"}</td>
                    <td>{job.acctId || "\u2014"}</td>
                    <td>{statusBadge(job.status)}</td>
                    <td>{statusBadge(job.numberStatus)}</td>
                    <td>{job.currentDay ?? "\u2014"}</td>
                    <td>{job.callsToday ?? "\u2014"}</td>
                    <td>{job.totalCalls ?? "\u2014"}</td>
                    <td>{lastCalled}</td>
                    <td>{job.carriers || "\u2014"}</td>
                    <td>{startDate}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Warmup Call Logs (Last 30) */}
      <h5 className="mt-4">Warmup Call Logs (Last 30)</h5>
      {callLogs.length === 0 ? (
        <div className="alert alert-info">No warmup call logs found for this phone number.</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm table-striped align-middle">
            <thead>
              <tr><th>Log ID</th><th>Called On</th><th>Duration</th><th>Device</th><th>Carrier</th><th>Success</th><th>Job ID</th><th></th></tr>
            </thead>
            <tbody>
              {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
              {callLogs.map((log: any, idx: number) => {
                const calledOn = log.calledOn ? formatLocal(new Date(log.calledOn)) : "\u2014";
                const device = formatPhoneE164(log.devicePhoneNumber);
                const duration = log.callDurationSecs != null ? log.callDurationSecs + "s" : "\u2014";
                const success = log.success === true
                  ? <span className="text-success fw-bold">Yes</span>
                  : log.success === false
                    ? <span className="text-danger fw-bold">No</span>
                    : "\u2014";
                const isOpen = expandedLogs.has(idx);
                return (
                  <React.Fragment key={idx}>
                    <tr>
                      <td>{log.warmupCallLogId}</td>
                      <td>{calledOn}</td>
                      <td>{duration}</td>
                      <td>{device}</td>
                      <td>{log.carrier || "\u2014"}</td>
                      <td>{success}</td>
                      <td>{log.warmupJobId || "\u2014"}</td>
                      <td><a href="#" className="text-muted" style={{ fontSize: 11 }} onClick={(e) => { e.preventDefault(); toggleLog(idx); }}>JSON</a></td>
                    </tr>
                    {isOpen && (
                      <tr><td colSpan={8} className="p-0">
                        <pre className="tc-json-block p-2 m-0 small" style={{ maxHeight: 300, overflow: "auto", whiteSpace: "pre-wrap" }}>
                          {JSON.stringify(log, null, 2)}
                        </pre>
                      </td></tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default WarmupDebugTab;
